import React, { useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
const pages = [{
  src: 'https://cdn.magicpatterns.com/uploads/tbpCWXN1hfTFcvc1TpVtzh/Captura_de_pantalla_2026-05-08_a_las_3.26.13_p.webp',
  title: 'Settimana 1: Mobilità e Respiro',
  tag: 'Programmazione Settimanale'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/3BaAJ6Nz8Rpu2HXzcHgMWc/Captura_de_pantalla_2026-05-08_a_las_3.26.30_p.webp',
  title: 'I Quattro Meccanismi del Feedback Tattile',
  tag: 'Fondamenti Biomeccanici'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/vzHgYMnQCrasf9RDgpwTn9/Captura_de_pantalla_2026-05-08_a_las_3.26.55_p.webp',
  title: 'Adattamenti Chair Pilates per Patologie Specifiche',
  tag: 'Protocolli Clinici'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/oBx2uJ21QgWbk7YRwuXeae/Captura_de_pantalla_2026-05-08_a_las_3.27.18_p.webp',
  title: 'La Sinergia Diaframma-Trasverso-Perineo',
  tag: 'Anatomia Funzionale'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nYMbpwnTat2xV23ni5A4SR/Captura_de_pantalla_2026-05-08_a_las_3.27.39_p.webp',
  title: 'Plyo-Pilates: Movimenti Esplosivi Controllati',
  tag: 'Progressioni Avanzate'
}];
export function MaterialPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const center = container.scrollLeft + container.clientWidth / 2;
    let minDistance = Infinity;
    let active = 0;
    Array.from(container.children).forEach((child, index) => {
      const el = child as HTMLElement;
      const childCenter = el.offsetLeft + el.clientWidth / 2;
      const distance = Math.abs(center - childCenter);
      if (distance < minDistance) {
        minDistance = distance;
        active = index;
      }
    });
    setActiveIndex(active);
  };
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const child = container.children[index] as HTMLElement;
    if (!child) return;
    const scrollLeft = child.offsetLeft - container.clientWidth / 2 + child.clientWidth / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };
  const scrollPrev = () => {
    if (activeIndex > 0) scrollToIndex(activeIndex - 1);
  };
  const scrollNext = () => {
    if (activeIndex < pages.length - 1) scrollToIndex(activeIndex + 1);
  };
  return <section className="w-full bg-bg-main py-20 px-4 relative overflow-hidden" data-id="element-147">
      {/* Soft decorative blobs */}
      <div className="absolute top-10 left-0 w-72 h-72 bg-primary/5 rounded-full blur-[80px] pointer-events-none" data-id="element-148"></div>
      <div className="absolute bottom-10 right-0 w-72 h-72 bg-secondary/5 rounded-full blur-[80px] pointer-events-none" data-id="element-149"></div>

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-150">
        <div className="text-center mb-12 sm:mb-16" data-id="element-151">
          <span className="inline-block bg-primary/10 text-primary text-xs sm:text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full border border-primary/20 mb-5" data-id="element-152">
            👀 Sbircia all'interno
          </span>
          <h2 className="text-3xl sm:text-5xl font-serif font-black text-text-main mb-4 tracking-tight" data-id="element-153">
            Pagine reali del Bundle
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-2xl mx-auto" data-id="element-154">
            Niente fumo, solo sostanza. Ecco un'anteprima delle pagine che
            riceverai oggi: layout chiari, illustrazioni didattiche e protocolli
            applicabili dalla prossima lezione.
          </p>
        </div>

        <div className="relative" data-id="element-155">
          {/* Navigation buttons (desktop) */}
          <button onClick={scrollPrev} className={`hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-bg-alt border border-border-light p-3 rounded-full text-text-main shadow-[0_4px_15px_rgba(201,137,155,0.15)] transition-all ${activeIndex === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Pagina precedente" data-id="element-156">
            <ChevronLeft className="w-5 h-5" data-id="element-157" />
          </button>
          <button onClick={scrollNext} className={`hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-bg-alt border border-border-light p-3 rounded-full text-text-main shadow-[0_4px_15px_rgba(201,137,155,0.15)] transition-all ${activeIndex === pages.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Pagina successiva" data-id="element-158">
            <ChevronRight className="w-5 h-5" data-id="element-159" />
          </button>

          {/* Carousel */}
          <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-5 sm:gap-6 px-[8vw] sm:px-[calc(50%-200px)] pb-8 pt-2" data-id="element-160">
            {pages.map((page, i) => <figure key={i} className={`snap-center shrink-0 w-[80vw] sm:w-[400px] bg-white border border-border-light rounded-2xl overflow-hidden flex flex-col shadow-[0_8px_30px_rgba(201,137,155,0.08)] transition-all duration-300 ${activeIndex === i ? 'scale-100 opacity-100' : 'scale-95 opacity-70'}`} data-id="element-161">
                <div className="relative bg-bg-alt p-3 sm:p-4 flex items-center justify-center" data-id="element-162">
                  <img src={page.src} alt={page.title} loading="lazy" decoding="async" width={800} height={1131} className="w-full h-auto max-h-[420px] sm:max-h-[480px] object-contain rounded-xl" data-id="element-163" />
                </div>
                <figcaption className="px-5 py-4 border-t border-border-light bg-white" data-id="element-164">
                  <span className="inline-block text-[10px] sm:text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-md mb-2" data-id="element-165">
                    {page.tag}
                  </span>
                  <h3 className="text-text-main font-bold text-sm sm:text-base leading-snug" data-id="element-166">
                    {page.title}
                  </h3>
                </figcaption>
              </figure>)}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-2" data-id="element-167">
            {pages.map((_, i) => <button key={i} onClick={() => scrollToIndex(i)} className={`h-2 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-primary w-8' : 'bg-border-light hover:bg-text-sec/50 w-2'}`} aria-label={`Vai alla pagina ${i + 1}`} data-id="element-168" />)}
          </div>

          {/* Counter */}
          <p className="text-center text-text-sec text-xs sm:text-sm font-medium mt-4" data-id="element-169">
            <span className="text-primary font-bold" data-id="element-170">{activeIndex + 1}</span>
            <span className="text-text-sec/60" data-id="element-171"> / {pages.length}</span>
            <span className="hidden sm:inline ml-2" data-id="element-172">
              — scorri per esplorare
            </span>
          </p>
        </div>
      </div>
    </section>;
}